import {
  Controller,
  Control,
  UseFormRegister,
  FieldValues,
  Path,
  FieldErrors,
  UseFormTrigger,
  useWatch,
} from "react-hook-form";
import { Check } from "lucide-react";
import {
  CheckBox,
  InputWithLabel,
  Select,
  TextInput,
} from "@normasteaching/norma-ui";
import { BusinessType } from "../MoneyTransfer/BusinessType.tsx";
import { italianProvinces } from "../MoneyTransfer/italianProvinces.ts";
import {
  billingCountries,
  isForeignBillingCountry,
} from "../MoneyTransfer/billingCountries.ts";
import { isArticle7TerSuggestedFor } from "../ProductService/BonificoOrder/article7Ter.ts";
import { ForeignBillingFields } from "./ForeignBillingFields.tsx";

export type BillingFormFields = {
  invoiceRequired: boolean;
  businessType?: string;
  businessName?: string;
  vat?: string;
  taxCode?: string;
  address?: {
    street?: string;
    cap?: string;
    city?: string;
    province?: string;
    country?: string;
  };
  pec?: string;
  sdiCode?: string;
};

type Props<T extends FieldValues & BillingFormFields> = {
  control: Control<T>;
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  invoiceRequired: boolean;
  businessType?: string;
  taxCode?: string;
  isBusinessInformationRequired: boolean;
  trigger: UseFormTrigger<T>;
  showCompanyOption?: boolean;
  heyLightNote?: React.ReactNode;
  isInvoiceMandatory?: boolean;
  askBillingCountry?: boolean;
  onArticle7TerSuggestion?: (isArticle7TerSuggested: boolean) => void;
};

export function BillingFormSection<T extends FieldValues & BillingFormFields>({
  control,
  register,
  errors,
  invoiceRequired,
  businessType,
  taxCode,
  isBusinessInformationRequired,
  trigger,
  showCompanyOption = true,
  heyLightNote,
  isInvoiceMandatory = false,
  askBillingCountry = false,
  onArticle7TerSuggestion,
}: Props<T>) {
  // Path<T> casts are safe: T extends BillingFormFields guarantees these keys exist
  const pecControl = register("pec" as Path<T>);
  const sdiCodeControl = register("sdiCode" as Path<T>);

  const billingErrors = errors as FieldErrors<BillingFormFields>;
  const billingCountry = useWatch({
    control,
    name: "address.country" as Path<T>,
  }) as string | undefined;
  const isForeignCustomer =
    askBillingCountry && isForeignBillingCountry(billingCountry);
  const suggestArticle7Ter = (customerProfile: {
    businessType?: string;
    country?: string;
  }) => onArticle7TerSuggestion?.(isArticle7TerSuggestedFor(customerProfile));

  const businessTypeOptions = [
    { id: "", name: "Seleziona tipo entità" },
    { id: BusinessType.Person, name: "Persona Fisica" },
    { id: BusinessType.IndividualCompany, name: "Ditta Individuale" },
    ...(showCompanyOption
      ? [{ id: BusinessType.Company, name: "Azienda" }]
      : []),
  ];

  const showVatAndElectronic =
    businessType === BusinessType.IndividualCompany ||
    businessType === BusinessType.Company;

  const showMainBillingFields =
    (businessType === BusinessType.IndividualCompany ||
      businessType === BusinessType.Person ||
      businessType === BusinessType.Company) &&
    isBusinessInformationRequired;

  return (
    <>
      {!isInvoiceMandatory && (
        <div id="richiestaFattura" className="pt-4">
          <div className="flex items-center gap-3">
            <Controller
              render={({ field }) => (
                <CheckBox
                  label={<span>Desidero la fattura</span>}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
              name={"invoiceRequired" as Path<T>}
              control={control}
            />
          </div>
        </div>
      )}

      {(invoiceRequired || isInvoiceMandatory) && (
        <div className="bg-gray-50 p-6 rounded-lg space-y-6">
          <h3 className="text-lg font-bold text-gray-900">Dati fatturazione</h3>

          {askBillingCountry && (
            <InputWithLabel label="Paese">
              <Controller
                render={({ field }) => (
                  <Select
                    searchable
                    testId="billing-country"
                    placeholder="Seleziona il paese"
                    value={field.value}
                    onChange={(country) => {
                      field.onChange(country);
                      suggestArticle7Ter({
                        businessType,
                        country: country ?? undefined,
                      });
                    }}
                    errorMessage={billingErrors.address?.country?.message}
                    options={billingCountries.map((country) => ({
                      name: country.name,
                      id: country.code,
                    }))}
                  />
                )}
                name={"address.country" as Path<T>}
                control={control}
              />
            </InputWithLabel>
          )}

          <div>
            <label className="block text-md font-bold text-gray-900 mb-2">
              Tipo entità
            </label>
            {heyLightNote}
            <Controller
              control={control}
              render={({ field }) => (
                <Select
                  errorMessage={billingErrors.businessType?.message}
                  value={field.value}
                  onChange={async (value) => {
                    field.onChange(value);
                    suggestArticle7Ter({
                      businessType: value ?? undefined,
                      country: billingCountry,
                    });
                    await trigger("businessType" as Path<T>);
                  }}
                  options={businessTypeOptions}
                />
              )}
              name={"businessType" as Path<T>}
            />
          </div>

          {isForeignCustomer && showMainBillingFields && (
            <ForeignBillingFields
              register={register}
              errors={billingErrors}
              businessType={businessType}
            />
          )}

          {!isForeignCustomer && showVatAndElectronic && (
            <div className="space-y-6">
              <div>
                <label className="block text-md font-bold text-gray-900 mb-2">
                  Partita IVA
                </label>
                <TextInput
                  {...register("vat" as Path<T>)}
                  errorMessage={billingErrors.vat?.message}
                  placeholder="12345678901"
                />
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800 font-medium">
                  Specificare almeno un campo tra PEC e Codice SDI:
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-md font-bold text-gray-900 mb-2">
                    PEC
                  </label>
                  <TextInput
                    type="email"
                    {...pecControl}
                    onChange={async (e) => {
                      await pecControl.onChange(e);
                      await trigger(["pec", "sdiCode"] as Path<T>[]);
                    }}
                    errorMessage={billingErrors.pec?.message}
                    placeholder="pec@example.com"
                  />
                </div>
                <div>
                  <label className="block text-md font-bold text-gray-900 mb-2">
                    Codice SDI
                  </label>
                  <TextInput
                    {...sdiCodeControl}
                    onChange={async (e) => {
                      await sdiCodeControl.onChange(e);
                      await trigger(["pec", "sdiCode"] as Path<T>[]);
                    }}
                    errorMessage={billingErrors.sdiCode?.message}
                    placeholder="ABC1234"
                  />
                </div>
              </div>
            </div>
          )}

          {businessType === BusinessType.Person &&
            !isBusinessInformationRequired && (
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-600" />
                  <p className="text-sm text-green-800 font-medium">
                    Nessun dato aggiuntivo richiesto per persona fisica
                  </p>
                </div>
              </div>
            )}

          {!isForeignCustomer && showMainBillingFields && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-md font-bold text-gray-900 mb-2">
                    Codice Fiscale
                  </label>
                  <TextInput
                    {...register("taxCode" as Path<T>)}
                    value={taxCode?.toUpperCase()}
                    maxLength={16}
                    errorMessage={billingErrors.taxCode?.message}
                    placeholder="Inserire il codice fiscale"
                  />
                </div>

                <div>
                  <label className="block text-md font-bold text-gray-900 mb-2">
                    Ragione Sociale
                  </label>
                  <TextInput
                    {...register("businessName" as Path<T>)}
                    errorMessage={billingErrors.businessName?.message}
                    disabled={businessType !== BusinessType.Company}
                    placeholder="Inserire ragione sociale"
                  />
                </div>

                <div>
                  <label className="block text-md font-bold text-gray-900 mb-2">
                    Indirizzo
                  </label>
                  <TextInput
                    {...register("address.street" as Path<T>)}
                    errorMessage={billingErrors.address?.street?.message}
                    placeholder="Inserire l'indirizzo di residenza"
                  />
                </div>

                <div>
                  <label className="block text-md font-bold text-gray-900 mb-2">
                    Codice Postale
                  </label>
                  <TextInput
                    {...register("address.cap" as Path<T>)}
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    errorMessage={billingErrors.address?.cap?.message}
                    placeholder="Codice Postale"
                  />
                </div>

                <div>
                  <label className="block text-md font-bold text-gray-900 mb-2">
                    Città
                  </label>
                  <TextInput
                    {...register("address.city" as Path<T>)}
                    errorMessage={billingErrors.address?.city?.message}
                    placeholder="Inserire la città di residenza"
                  />
                </div>

                <div>
                  <InputWithLabel label="Provincia">
                    <Controller
                      render={({ field }) => (
                        <Select
                          searchable
                          placeholder="Seleziona la provincia"
                          value={field.value}
                          onChange={field.onChange}
                          errorMessage={
                            billingErrors.address?.province?.message
                          }
                          options={italianProvinces.map((p) => ({
                            name: p.nome,
                            id: p.sigla,
                          }))}
                        />
                      )}
                      name={"address.province" as Path<T>}
                      control={control}
                    />
                  </InputWithLabel>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
